import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, BookOpen, Users, Award, FileText, Calendar, Filter, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const SearchResultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const filterParam = searchParams.get('filter') || 'all';
  
  const [results, setResults] = useState({
    library: [],
    people: [],
    pages: [],
    projects: [],
    events: [],
    news: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(filterParam);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  useEffect(() => {
    setActiveFilter(filterParam);
  }, [filterParam]);

  const performSearch = async (searchQuery) => {
    setIsLoading(true);
    const lowerQuery = searchQuery.toLowerCase();

    // Search all content sources with relevance ranking
    const searchResults = {
      library: await searchLibrary(lowerQuery),
      people: searchPeople(lowerQuery),
      pages: searchPages(lowerQuery),
      projects: searchProjects(lowerQuery),
      events: searchEvents(lowerQuery),
      news: searchNews(lowerQuery)
    };

    setResults(searchResults);
    setIsLoading(false);
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('filter', filter);
    setSearchParams(newParams);
  };

  const searchLibrary = async (query) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/library-search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        return data.results.slice(0, 5).map(book => ({
          title: book.title,
          description: book.author ? `by ${book.author}` : '',
          link: '/library/catalogue?q=' + encodeURIComponent(query),
          metadata: book.publicationYear || ''
        }));
      }
    } catch (error) {
      console.error('Library search error:', error);
    }
    return [];
  };

  const searchPeople = (query) => {
    // Import people data dynamically
    const allPeople = [
      // Leadership
      { id: "michael-heneise", name: "Michael Heneise", title: "President", category: "Leadership Council", role: "President" },
      { id: "catriona-child", name: "Catriona Child", title: "Executive Vice President & Executive Director", category: "Leadership Council", role: "Executive Vice President" },
      // Honorary VPs
      { id: "vizovol-mekro", name: "Vizovol Mekro", title: "Honorary Vice President", category: "Honorary Vice Presidents", role: "Honorary Vice President" },
      { id: "theyie-keditsu", name: "Theyie Keditsu", title: "Honorary Vice President", category: "Honorary Vice Presidents", role: "Honorary Vice President" },
      { id: "zavise-rume", name: "Zavise Rume", title: "Honorary Vice President", category: "Honorary Vice Presidents", role: "Honorary Vice President" },
      { id: "viketoulie-pienyu", name: "Viketoulie Pienyü", title: "Honorary Vice President", category: "Honorary Vice Presidents", role: "Honorary Vice President" },
      { id: "arkotong-longkumer", name: "Arkotong Longkumer", title: "Honorary Vice President", category: "Honorary Vice Presidents", role: "Honorary Vice President" },
      // Senior Staff
      { id: "ruokuonuo-yhome", name: "Ruokuonuo Yhome", title: "Research Director", category: "Senior Staff", role: "Research Director" },
      { id: "rovithono-yhome", name: "Rovithono Yhome", title: "Human Resource Manager", category: "Senior Staff", role: "Human Resource Manager" },
      { id: "lanuakum-aier", name: "Lanuakum Aier", title: "Operations Manager", category: "Senior Staff", role: "Operations Manager" },
      // Research Assistants
      { id: "saktum-wonti", name: "Saktum Wonti", title: "Research Assistant", category: "Research Assistants", role: "Research Assistant" },
      { id: "tumuzo-katiry", name: "Tümüzo Katiry", title: "Research Assistant", category: "Research Assistants", role: "Research Assistant" },
      { id: "kevide-lcho", name: "Kevide Lcho", title: "Research Assistant", category: "Research Assistants", role: "Research Assistant" },
      { id: "sholu-movi", name: "Sholu Movi", title: "Research Assistant", category: "Research Assistants", role: "Research Assistant" },
      { id: "esther-swu", name: "Esther Swu", title: "Research Assistant", category: "Research Assistants", role: "Research Assistant" },
      // Fellows (selection)
      { id: "jonathon-keats", name: "Jonathon Keats", title: "Highland Fellow", category: "Fellows & Visiting Scholars", role: "Highland Fellow" },
      { id: "dharamsing-teron", name: "Dharamsing Teron", title: "Highland Fellow", category: "Fellows & Visiting Scholars", role: "Highland Fellow" },
      { id: "jelle-j-p-wouters", name: "Jelle J P Wouters", title: "Highland Fellow", category: "Fellows & Visiting Scholars", role: "Highland Fellow" },
      { id: "ruokuonuo-rose-yhome", name: "Ruokuonuo Rose Yhome", title: "Highland Fellow", category: "Fellows & Visiting Scholars", role: "Highland Fellow" }
    ];

    // Calculate relevance score for ranking
    const scored = allPeople.filter(person => {
      const nameMatch = person.name.toLowerCase().includes(query);
      const titleMatch = person.title.toLowerCase().includes(query);
      const categoryMatch = person.category.toLowerCase().includes(query);
      return nameMatch || titleMatch || categoryMatch;
    }).map(person => {
      let score = 0;
      const nameLower = person.name.toLowerCase();
      const titleLower = person.title.toLowerCase();
      
      // Exact name match gets highest score
      if (nameLower === query) score += 100;
      // Name starts with query
      else if (nameLower.startsWith(query)) score += 50;
      // Name contains query
      else if (nameLower.includes(query)) score += 25;
      
      // Title matches
      if (titleLower.includes(query)) score += 10;
      
      return {
        ...person,
        score
      };
    });

    // Sort by relevance score
    scored.sort((a, b) => b.score - a.score);

    return scored.map(person => ({
      title: person.name,
      description: `${person.role} • ${person.category}`,
      link: `/people/${person.id}`,
      type: 'Person',
      metadata: person.category
    }));
  };

  const searchPages = (query) => {
    const pages = [
      { title: "About Us", description: "Learn about the Highland Institute's mission and vision", link: "/about", keywords: "about mission vision institute highland history", type: "Page" },
      { title: "Research", description: "Explore our research themes and projects", link: "/research", keywords: "research projects themes methodology", type: "Page" },
      { title: "Study With Us", description: "Academic programmes and courses", link: "/programmes", keywords: "study programmes courses education masters phd fellowship", type: "Page" },
      { title: "Library & Publications", description: "Access our catalogue of 900+ publications", link: "/library", keywords: "library books publications catalogue resources", type: "Page" },
      { title: "Ekologos", description: "Environmental Humanities Initiative", link: "/ekologos", keywords: "ekologos environment humanities climate ecology", type: "Page" },
      { title: "Contact", description: "Get in touch with the Highland Institute", link: "/contact", keywords: "contact address location kohima nagaland", type: "Page" },
      { title: "People", description: "Our team of scholars, researchers, and practitioners", link: "/people", keywords: "people staff team scholars fellows", type: "Page" },
      { title: "Leadership Council", description: "Institute leadership and governance", link: "/staff", keywords: "leadership council president director governance", type: "Page" },
      { title: "Fellows & Visiting Scholars", description: "Our global fellowship community", link: "/fellows", keywords: "fellows scholars visiting community", type: "Page" }
    ];

    const scored = pages.filter(page => 
      page.title.toLowerCase().includes(query) ||
      page.description.toLowerCase().includes(query) ||
      page.keywords.toLowerCase().includes(query)
    ).map(page => {
      let score = 0;
      const titleLower = page.title.toLowerCase();
      const descLower = page.description.toLowerCase();
      
      // Title matches are most relevant
      if (titleLower === query) score += 100;
      else if (titleLower.includes(query)) score += 50;
      
      // Description matches
      if (descLower.includes(query)) score += 25;
      
      // Keyword matches
      if (page.keywords.includes(query)) score += 15;
      
      return { ...page, score };
    });

    scored.sort((a, b) => b.score - a.score);

    return scored.map(page => ({
      title: page.title,
      description: page.description,
      link: page.link,
      type: page.type
    }));
  };

  const searchProjects = (query) => {
    const projects = [
      { title: "Urbaltour Project", description: "Urban tourism sustainability in Kohima", link: "/urbaltour", keywords: "tourism urban kohima sustainability hornbill nagaland", status: "Completed", type: "Project" },
      { title: "Environmental Humanities", description: "Climate change and indigenous knowledge", link: "/ekologos", keywords: "environment climate indigenous knowledge ecology", status: "Ongoing", type: "Project" },
      { title: "Highland Studies", description: "Research on highland communities and cultures", link: "/research", keywords: "highland communities culture anthropology", status: "Ongoing", type: "Project" },
      { title: "Medical Pluralism", description: "Healthcare practices in Northeast India", link: "/research", keywords: "health medical pluralism healthcare traditional", status: "Ongoing", type: "Project" }
    ];

    const scored = projects.filter(project => 
      project.title.toLowerCase().includes(query) ||
      project.description.toLowerCase().includes(query) ||
      project.keywords.toLowerCase().includes(query)
    ).map(project => {
      let score = 0;
      const titleLower = project.title.toLowerCase();
      
      if (titleLower === query) score += 100;
      else if (titleLower.includes(query)) score += 50;
      if (project.description.toLowerCase().includes(query)) score += 25;
      if (project.keywords.includes(query)) score += 15;
      
      return { ...project, score };
    });

    scored.sort((a, b) => b.score - a.score);

    return scored.map(project => ({
      title: project.title,
      description: project.description,
      link: project.link,
      type: project.type,
      metadata: project.status
    }));
  };

  const searchNews = (query) => {
    const news = [
      { title: "Report highlights need for sustainable tourism in Kohima", description: "Research findings on year-round tourism strategy beyond Hornbill Festival", link: "/urbaltour", keywords: "tourism kohima hornbill sustainability", date: "2024", type: "News" },
      { title: "12th Annual Ursula Graham Bower Lectures", description: "Dr Bérénice Guyot-Réchard on Burma and India's Unfinished Separation", link: "/bower-lectures", keywords: "bower lectures burma india separation", date: "2024", type: "Event" }
    ];

    return news.filter(item => 
      item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      item.keywords.toLowerCase().includes(query)
    ).map(item => ({
      title: item.title,
      description: item.description,
      link: item.link,
      type: item.type,
      metadata: item.date
    }));
  };

  const searchEvents = (query) => {
    const events = [
      { title: "Ursula Graham Bower Lectures", description: "Annual lecture series", link: "/events", keywords: "bower lectures annual" },
      { title: "British Academy Workshops", description: "Academic writing training", link: "/events", keywords: "british academy workshops writing" },
      { title: "Ekologos Conference", description: "Environmental Humanities conference", link: "/events", keywords: "ekologos conference environment" }
    ];

    return events.filter(event => 
      event.title.toLowerCase().includes(query) ||
      event.description.toLowerCase().includes(query) ||
      event.keywords.toLowerCase().includes(query)
    ).map(event => ({
      title: event.title,
      description: event.description,
      link: event.link
    }));
  };

  const getFilteredResults = () => {
    if (activeFilter === 'all') return results;
    if (activeFilter === 'website') {
      return {
        library: [],
        people: results.people,
        pages: results.pages,
        projects: results.projects,
        events: results.events,
        news: results.news
      };
    }
    if (activeFilter === 'library') {
      return {
        library: results.library,
        people: [],
        pages: [],
        projects: [],
        events: [],
        news: []
      };
    }
    if (activeFilter === 'people') {
      return {
        library: [],
        people: results.people,
        pages: [],
        projects: [],
        events: [],
        news: []
      };
    }
    if (activeFilter === 'pages') {
      return {
        library: [],
        people: [],
        pages: results.pages,
        projects: [],
        events: [],
        news: []
      };
    }
    if (activeFilter === 'projects') {
      return {
        library: [],
        people: [],
        pages: [],
        projects: results.projects,
        events: [],
        news: []
      };
    }
    if (activeFilter === 'events') {
      return {
        library: [],
        people: [],
        pages: [],
        projects: [],
        events: [...results.events, ...results.news],
        news: []
      };
    }
    return results;
  };

  const filteredResults = getFilteredResults();
  const totalResults = Object.values(filteredResults).reduce((sum, arr) => sum + arr.length, 0);
  const allResults = Object.values(results).reduce((sum, arr) => sum + arr.length, 0);

  const filters = [
    { id: 'all', label: 'All Results', count: allResults },
    { id: 'website', label: 'Website Only', count: results.people.length + results.pages.length + results.projects.length + results.events.length + results.news.length },
    { id: 'library', label: 'Library Only', count: results.library.length },
    { id: 'people', label: 'People', count: results.people.length },
    { id: 'pages', label: 'Pages', count: results.pages.length },
    { id: 'projects', label: 'Projects', count: results.projects.length },
    { id: 'events', label: 'Events & News', count: results.events.length + results.news.length }
  ];

  const ResultSection = ({ title, icon: Icon, items, color }) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-4 pb-2 border-b-2 border-stone-200">
          <div className={`w-10 h-10 ${color} rounded-full flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-green-700" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-stone-900">{title}</h2>
          <span className="text-sm text-stone-500">({items.length})</span>
        </div>
        <div className="space-y-3">
          {items.map((item, index) => (
            <Link key={index} to={item.link}>
              <Card className="hover:shadow-lg transition-all hover:border-green-500 cursor-pointer border border-stone-200">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {item.type && (
                          <span className="text-xs font-medium px-2 py-0.5 bg-green-100 text-green-700 rounded">
                            {item.type}
                          </span>
                        )}
                        {item.metadata && (
                          <span className="text-xs text-stone-500">{item.metadata}</span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-stone-900 mb-1 hover:text-green-600 transition-colors">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-stone-600 leading-relaxed">{item.description}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-900 to-green-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl font-serif font-bold mb-3">Search Results</h1>
            {query && (
              <p className="text-xl text-green-100">
                Results for: <span className="font-semibold">"{query}"</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="max-w-5xl mx-auto">
            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 bg-stone-100 rounded-md mb-3 w-full justify-between"
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <span className="font-medium">Filters</span>
              </span>
              <span className="text-xs text-stone-600">
                {filters.find(f => f.id === activeFilter)?.label}
              </span>
            </button>

            {/* Filters */}
            <div className={`${showFilters ? 'flex' : 'hidden'} md:flex flex-wrap gap-2`}>
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilter === filter.id
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  {filter.label}
                  <span className="ml-2 text-xs opacity-75">({filter.count})</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-stone-600 font-serif">Searching across website and library catalogue...</p>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-stone-400 mx-auto mb-6" />
              <h2 className="text-3xl font-serif font-bold text-stone-900 mb-4">No results found</h2>
              
              {/* Check if we have partial results */}
              {activeFilter === 'all' && allResults === 0 ? (
                <>
                  <p className="text-stone-600 mb-3 max-w-2xl mx-auto leading-relaxed">
                    No results found for <span className="font-semibold">"{query}"</span>.
                  </p>
                  <p className="text-stone-500 mb-8 text-sm max-w-2xl mx-auto">
                    Try a different keyword, a place name (e.g., "Nagaland", "Kohima"), a researcher's name, 
                    or a topic such as "medical pluralism", "environment", or "dreams".
                  </p>
                  <div className="bg-stone-100 rounded-lg p-6 max-w-xl mx-auto mb-6">
                    <h3 className="font-semibold text-stone-900 mb-3">Suggestions:</h3>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Link to="/search?q=research" className="px-3 py-1 bg-white rounded-full text-sm hover:bg-green-50 transition-colors">research</Link>
                      <Link to="/search?q=nagaland" className="px-3 py-1 bg-white rounded-full text-sm hover:bg-green-50 transition-colors">nagaland</Link>
                      <Link to="/search?q=environment" className="px-3 py-1 bg-white rounded-full text-sm hover:bg-green-50 transition-colors">environment</Link>
                      <Link to="/search?q=fellowship" className="px-3 py-1 bg-white rounded-full text-sm hover:bg-green-50 transition-colors">fellowship</Link>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-stone-600 mb-6">
                  No results in this category. Try selecting "All Results" or a different filter.
                </p>
              )}
              
              <Link to="/">
                <button className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium">
                  Back to Home
                </button>
              </Link>
            </div>
          ) : (
            <>
              {/* Result Summary */}
              <div className="mb-8 pb-6 border-b-2 border-stone-200">
                <p className="text-stone-700 text-lg">
                  Found <span className="font-bold text-green-600">{totalResults}</span> {totalResults === 1 ? 'result' : 'results'}
                  {activeFilter !== 'all' && <span> in <span className="font-semibold">{filters.find(f => f.id === activeFilter)?.label}</span></span>}
                </p>
                {activeFilter === 'all' && filteredResults.library.length === 0 && allResults > 0 && (
                  <p className="text-sm text-stone-500 mt-2">
                    ℹ️ No library catalogue items found, but showing website results below.
                  </p>
                )}
                {activeFilter === 'all' && filteredResults.library.length > 0 && (filteredResults.people.length + filteredResults.pages.length + filteredResults.projects.length) === 0 && (
                  <p className="text-sm text-stone-500 mt-2">
                    ℹ️ No website pages found, but showing library catalogue results below.
                  </p>
                )}
              </div>

              {/* Results by Section */}
              <ResultSection 
                title="People Profiles" 
                icon={Users} 
                items={filteredResults.people}
                color="bg-blue-50"
              />

              <ResultSection 
                title="Pages & Information" 
                icon={FileText} 
                items={filteredResults.pages}
                color="bg-purple-50"
              />

              <ResultSection 
                title="Research Projects" 
                icon={FileText} 
                items={filteredResults.projects}
                color="bg-pink-50"
              />

              <ResultSection 
                title="Events & News" 
                icon={Calendar} 
                items={[...filteredResults.events, ...filteredResults.news]}
                color="bg-orange-50"
              />

              <ResultSection 
                title="Library Catalogue" 
                icon={BookOpen} 
                items={filteredResults.library}
                color="bg-green-50"
              />
            </>
          )}
        </div>
      </div>

      {/* Footer Note */}
      {!isLoading && totalResults > 0 && (
        <div className="bg-white border-t border-stone-200 py-8 mt-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto text-center">
              <p className="text-sm text-stone-600 font-serif italic leading-relaxed">
                This search covers all Highland Institute website content and our library catalogue of 900+ publications. 
                For detailed library searches, visit the <Link to="/library/catalogue" className="text-green-600 hover:underline">Library Catalogue</Link>.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResultsPage;
